// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Check } from '@/constants/hardfin-standard-icons';
import type { CsvImportStep } from '@/csv-import/types';
import {
  Box,
  Stepper as MuiStepper,
  Step,
  StepConnector,
  StepIconProps,
  StepLabel,
  stepConnectorClasses,
  styled,
} from '@mui/material';

interface CsvImportStepperProps {
  currentStep: CsvImportStep;
}

const steps: { key: CsvImportStep; label: string }[] = [
  { key: 'download-template', label: 'Download template' },
  { key: 'upload-csv', label: 'Upload CSV' },
  { key: 'results', label: 'Results' },
];

const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 15,
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 1,
    border: 0,
    backgroundColor: theme.palette.divider,
  },
}));

function CustomStepIcon(props: StepIconProps) {
  const { active, completed, icon } = props;

  return (
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: '50%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontWeight: 'bold',
        fontSize: '14px',
        backgroundColor: completed
          ? 'success.main'
          : active
            ? 'primary.main'
            : 'background.paper',
        border: (theme) =>
          `2px solid ${
            completed
              ? theme.palette.success.main
              : active
                ? theme.palette.primary.main
                : theme.palette.divider
          }`,
        color: completed || active ? 'white' : 'text.secondary',
      }}
    >
      {completed ? <Check /> : icon}
    </Box>
  );
}

export function CsvImportStepper(props: CsvImportStepperProps) {
  const currentStepIndex = steps.findIndex(
    (step) => step.key === props.currentStep
  );

  return (
    <MuiStepper
      alternativeLabel
      activeStep={currentStepIndex}
      connector={<CustomConnector />}
      sx={{ mb: 4 }}
    >
      {steps.map((step, index) => {
        const isCurrent = index === currentStepIndex;

        return (
          <Step key={step.key}>
            <StepLabel
              StepIconComponent={CustomStepIcon}
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: 'caption.fontSize',
                  fontWeight: isCurrent ? 'bold' : 'normal',
                  color: isCurrent ? 'primary.main' : 'text.secondary',
                },
              }}
            >
              {step.label}
            </StepLabel>
          </Step>
        );
      })}
    </MuiStepper>
  );
}
